import { RCXAdapter } from 'rcx_adapter';
import { i18n } from 'rcx_i18n';

import { NOT_SYNC_STATE, rcxDefaultStateIdList, rcxNeedStateIdList } from '../constants';
import { DataFetch } from '../lib/DataFetch';
import { catchError } from '../utils/catchError';
export class StateSyncAdapter {
    private _rcxAdapter: RCXAdapter;
    private _dataFetch: DataFetch;
    private _availableAgentStates: any[] = [];
    private _serviceChannels: any[] = [];
    private _servicenowStates: any[] = [];
    private _servicenowStatesList: any[] = [];
    private _preSNState: string = '';
    private _preRCXState: string = '';
    private _isStateSyncActive: boolean = false;
    private _isUpdatingSNState: boolean = false;
    private _isUpdatingRCXState: boolean = false;
    private _ringCXToServicenowMap: { [key: string]: string } = {};
    private _servicenowToRingCXMap: { [key: string]: string } = {};
    private _domain: string;
    constructor(dataFetch: DataFetch, rcxAdapter: RCXAdapter, domain: string) {
        this._dataFetch = dataFetch;
        this._rcxAdapter = rcxAdapter;
        this._domain = domain;
    }

    /**
     * Gets default mapping from RingCX to ServiceNow states
     */
    private _getDefaultRingCXToServicenowMap() {
        if (this._servicenowStatesList.length > 0) {
            const firstAvailableState =
                this._servicenowStatesList.find((state) => {
                    const ids = state.split(',');
                    if (ids[1] && this._serviceChannels.find((ch) => ch.type === 'phone' && ids[1] === ch.sys_id)) {
                        return true;
                    }
                    return false;
                }) || '';
            const firstBusyState =
                this._servicenowStates.find(
                    (state) =>
                        state.active === 'true' &&
                        state.available_to_receive_work_items === 'false' &&
                        state.name !== 'Offline'
                )?.sys_id || '';
            return rcxNeedStateIdList.reduce((acc: any, id: string) => {
                acc[id] = id === 'AVAILABLE' ? firstAvailableState : id === 'WORKING' ? firstBusyState : NOT_SYNC_STATE;
                return acc;
            }, {});
        } else {
            return {};
        }
    }

    /**
     * Gets RingCX state name in proper format
     */
    private _getRcxStateName(rcxStateId: string) {
        return rcxStateId.charAt(0).toUpperCase() + rcxStateId.slice(1).toLowerCase();
    }

    /**
     * Gets default RingCX state based on ServiceNow state
     */
    private _getDefaultRingCXState(state: string) {
        const ids = state.split(',');
        if (ids[1] && this._serviceChannels.find((ch) => ch.type === 'phone' && ids[1] === ch.sys_id)) {
            return 'AVAILABLE';
        }
        return 'WORKING';
    }

    /**
     * Gets default mapping from ServiceNow to RingCX states
     */
    private _getDefaultServicenowToRingCXMap() {
        return this._servicenowStatesList.reduce((acc: any, state: any) => {
            acc[state] = this._getDefaultRingCXState(state);
            return acc;
        }, {});
    }

    public onAgentPresenceStateChange(data: any): void {
        const { available, channels, sys_id } = data?.result?.presence || {};

        let SNStateId = sys_id;
        let showError = false;
        let RCXState = this._servicenowToRingCXMap[SNStateId];
        if (available) {
            const callChannelId = channels?.find((channel: any) => channel.service_channel_type === 'phone')?.sys_id;
            if (callChannelId) {
                SNStateId = `${sys_id},${callChannelId}`;
                if (this._servicenowToRingCXMap[SNStateId] === 'AVAILABLE') {
                    RCXState = 'AVAILABLE';
                } else {
                    RCXState = NOT_SYNC_STATE;
                    showError = true;
                }
            } else {
                SNStateId = `${sys_id},${channels[0]?.sys_id}`;
                RCXState = this._servicenowToRingCXMap[SNStateId];
            }
        }
        if (
            this._isStateSyncActive &&
            this._preSNState !== SNStateId &&
            this._preRCXState !== RCXState &&
            !this._isUpdatingRCXState &&
            !this._isUpdatingSNState
        ) {
            if (showError) {
                window.alert(i18n.t('state_mapping_error'));
                this._preSNState = SNStateId;
                this._isUpdatingSNState = false;
                return;
            }
            if (
                RCXState &&
                RCXState !== NOT_SYNC_STATE &&
                this._availableAgentStates.find((state) => state.agentState === RCXState)
            ) {
                this._isUpdatingRCXState = true;
                this._rcxAdapter.updateRCXState(RCXState);
            }
            if (
                rcxDefaultStateIdList.includes(RCXState) &&
                !this._availableAgentStates.find((state) => state.agentState === RCXState)
            ) {
                const rcxStateName = this._getRcxStateName(RCXState);
                window.alert(
                    i18n.t('rcx_state_not_found_error', {
                        rcxStateName,
                        link: 'https://support.ringcentral.com/article-v2/Configuring-agent-states-in-your-RingCX-account.html?brand=RingCentral&product=RingCX&language=en_US',
                    })
                );
            }
        }
        this._preSNState = SNStateId;
        this._isUpdatingSNState = false;
    }

    /**
     * Gets and initializes the state sync mapping
     */
    @catchError
    public async getStatesSyncMap() {
        const { result = [] } = (await this._dataFetch.getStatesSyncMap()) || {};
        const mapConfig = result[0] || {};
        this._isStateSyncActive = mapConfig?.u_active === 'true' || false;
        try {
            this._ringCXToServicenowMap = mapConfig?.u_ringcx_to_servicenow
                ? JSON.parse(mapConfig?.u_ringcx_to_servicenow)
                : this._getDefaultRingCXToServicenowMap();
        } catch {
            this._ringCXToServicenowMap = this._getDefaultRingCXToServicenowMap();
        }
        try {
            this._servicenowToRingCXMap = mapConfig?.u_servicenow_to_ringcx
                ? JSON.parse(mapConfig?.u_servicenow_to_ringcx)
                : this._getDefaultServicenowToRingCXMap();
        } catch {
            this._servicenowToRingCXMap = this._getDefaultServicenowToRingCXMap();
        }
    }

    /**
     * Updates the presence state
     */
    public updatePresenceState(state: string) {
        const stateId = this._ringCXToServicenowMap[state];
        const currentUserId = localStorage.getItem('currentUserId');
        if (
            this._isStateSyncActive &&
            this._preSNState !== stateId &&
            this._preRCXState !== state &&
            !this._isUpdatingSNState &&
            !this._isUpdatingRCXState
        ) {
            if (stateId) {
                const ids = stateId.split(',');
                if (
                    state === 'AVAILABLE' &&
                    ids[1] &&
                    !this._serviceChannels.find((ch) => ch.type === 'phone' && ids[1] === ch.sys_id)
                ) {
                    window.alert(i18n.t('state_mapping_error'));
                    this._preRCXState = state;
                    this._isUpdatingRCXState = false;
                    return;
                }
                if (!currentUserId) {
                    window.alert(
                        i18n.t('user_not_found_error', {
                            link: `${this._domain}navpage.do`,
                        })
                    );
                    this._preRCXState = state;
                    this._isUpdatingRCXState = false;
                    return;
                }
                if (stateId !== NOT_SYNC_STATE) {
                    this._isUpdatingSNState = true;
                    this._dataFetch.updatePresenceState(currentUserId, stateId).catch((error) => {
                        window.alert(i18n.t('state_mapping_error'));
                        console.error(error);
                        this._isUpdatingSNState = false;
                    });
                }
            } else if (rcxNeedStateIdList.includes(state) && !stateId) {
                window.alert(i18n.t('state_mapping_error'));
            }
        }

        this._preRCXState = state;
        this._isUpdatingRCXState = false;
    }

    /**
     * Gets the current presence state
     */
    @catchError
    public async getPresenceState() {
        const currentUserId = localStorage.getItem('currentUserId');
        if (currentUserId) {
            const { result } = await this._dataFetch.getPresenceState(currentUserId);
            let stateId = result?.presence?.sys_id;
            if (result?.presence?.channels?.length > 0) {
                let channelId = result?.presence?.channels[0]?.sys_id;
                const phoneChannel = result?.presence?.channels.find((ch: any) => ch.service_channel_type === 'phone');
                if (phoneChannel) {
                    channelId = phoneChannel.sys_id;
                }
                stateId = `${stateId},${channelId}`;
            }
            this._preSNState = stateId || '';
        }
    }

    /**
     * Sets the available agent states
     */
    public setAvailableAgentStates(states: any[]) {
        this._availableAgentStates = states;
    }

    /**
     * Gets and processes presence states
     */
    @catchError
    public async getPresenceStates() {
        const { result = [] } = await this._dataFetch.getPresenceStates();
        this._servicenowStates = result;
        this._servicenowStatesList = result.reduce((acc: any, state: any) => {
            if (state.active === 'true') {
                if (state.service_channels) {
                    state.service_channels.split(',').forEach((channelId: string) => {
                        acc.push(`${state.sys_id},${channelId}`);
                    });
                } else {
                    acc.push(state.sys_id);
                }
            }
            return acc;
        }, []);
    }

    /**
     * Gets service channel information
     */
    @catchError
    public async getServiceChannel() {
        const { result = [] } = await this._dataFetch.getServiceChannel();
        this._serviceChannels = result;
    }
}
